import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { styles, colors, fmtDate, useIsMobile } from '../components/UI';

// Emoji per known category for a quick visual scan; custom ones fall back to 📁.
const CAT_EMOJI = {
  Presentations: '📊', Literature: '📄', 'Spec Sheets': '📋',
  Platforms: '🧰', 'Case Studies': '📈', Other: '📁'
};
const catIcon = (name) => CAT_EMOJI[name] || '📁';

const fmtSize = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// File-type icon from the extension.
const extIcon = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['ppt', 'pptx', 'key'].includes(ext)) return '📊';
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx'].includes(ext)) return '📘';
  if (['xls', 'xlsx'].includes(ext)) return '📗';
  if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return '🖼️';
  if (['mp4', 'mov'].includes(ext)) return '🎬';
  if (ext === 'zip') return '🗜️';
  return '📄';
};

const stripExt = (name) => (name || '').replace(/\.[^.]+$/, '');

export default function SalesLibrary() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canManage = user.role === 'admin' || user.role === 'leadership';

  const [cats, setCats] = useState([]);
  const [selectedCat, setSelectedCat] = useState(''); // '' = All
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadCats = async () => {
    try { setCats(await api.salesLibraryCategories()); } catch (e) { setError(e.message); }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setFiles(await api.listSalesLibrary({ q, category: selectedCat }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, selectedCat]);

  // Block the browser's default file-drop behavior across the whole window.
  // Without this, a drop that lands even slightly outside the dashed zone
  // makes the browser navigate to (open) the file instead of running our
  // handler — the file never uploads. Preventing the window default keeps the
  // drop ours; the zone's own onDrop still does the upload.
  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  const newCategory = async () => {
    const name = window.prompt('New category name (e.g. Compressors, Trailers, Spec Sheets):', '');
    if (name === null) return;
    if (!name.trim()) return;
    try {
      const created = await api.createSalesLibraryCategory(name.trim());
      await loadCats();
      setSelectedCat(created.name); // jump into the new folder, ready for drops
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteCategory = async (cat, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the "${cat.name}" category? (Only works if it has no files.)`)) return;
    try {
      await api.deleteSalesLibraryCategory(cat.id);
      if (selectedCat === cat.name) setSelectedCat('');
      await loadCats();
    } catch (e2) {
      setError(e2.message);
    }
  };

  // Upload one or more files straight into the selected folder.
  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    if (!selectedCat) {
      setError('Pick a category first (or create one), then drop files into it.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      for (const f of arr) {
        await api.uploadSalesLibrary({ file: f, title: stripExt(f.name), category: selectedCat });
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
      await loadCats();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  // Pull files from a drop event. Prefer dataTransfer.files; fall back to
  // dataTransfer.items (some Windows drag sources only populate items).
  const filesFromDrop = (e) => {
    const dt = e.dataTransfer;
    if (!dt) return [];
    if (dt.files && dt.files.length) return Array.from(dt.files);
    if (dt.items && dt.items.length) {
      return Array.from(dt.items)
        .filter(i => i.kind === 'file')
        .map(i => i.getAsFile())
        .filter(Boolean);
    }
    return [];
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!selectedCat) {
      setError('Pick a category first (or create one), then drop files into it.');
      return;
    }
    const dropped = filesFromDrop(e);
    if (!dropped.length) {
      setError('No file detected in that drop. Try the "click to browse" option instead.');
      return;
    }
    uploadFiles(dropped);
  };

  const doDownload = async (f) => {
    try {
      await api.downloadSalesLibrary(f.id, f.original_name);
      setFiles(fs => fs.map(x => x.id === f.id ? { ...x, download_count: Number(x.download_count) + 1 } : x));
    } catch (e) { setError(e.message); }
  };

  const doDelete = async (f) => {
    if (!window.confirm(`Delete "${f.title}" from the Sales Library? This cannot be undone.`)) return;
    try {
      await api.deleteSalesLibrary(f.id);
      setFiles(fs => fs.filter(x => x.id !== f.id));
      loadCats();
    } catch (e) { setError(e.message); }
  };

  const doRename = async (f) => {
    const title = window.prompt('Title:', f.title);
    if (title === null || !title.trim()) return;
    try {
      const updated = await api.updateSalesLibrary(f.id, { title: title.trim() });
      setFiles(fs => fs.map(x => x.id === f.id ? { ...x, ...updated, uploaded_by_name: x.uploaded_by_name } : x));
    } catch (e) { setError(e.message); }
  };

  const chip = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
    border: `1px solid ${active ? colors.orange : colors.border}`,
    background: active ? '#FFF7ED' : '#fff',
    color: active ? colors.orangeDark : colors.text,
    fontWeight: active ? 700 : 500,
    boxShadow: active ? 'inset 0 2px 3px rgba(0, 0, 0, 0.15)' : '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)'
  });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: colors.charcoal }}>📚 Sales Library</h1>
        <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 2 }}>
          Select Safety presentations, platforms, and sales literature. Pick a category, then drop files into it. Anyone can upload and download.
        </div>
      </div>

      {error && (
        <div style={{ background: colors.redSoft, color: colors.red, padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Category folders */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 2 }}>Categories:</span>
        <div style={chip(selectedCat === '')} onClick={() => setSelectedCat('')}>📚 All</div>
        {cats.map(c => (
          <div key={c.id} style={chip(selectedCat === c.name)} onClick={() => setSelectedCat(c.name)}>
            {catIcon(c.name)} {c.name} <span style={{ color: colors.textMuted, fontWeight: 500 }}>({c.file_count})</span>
            {canManage && (
              <span
                title="Delete category (must be empty)"
                onClick={(e) => deleteCategory(c, e)}
                style={{ marginLeft: 2, color: colors.textMuted, fontWeight: 700, cursor: 'pointer' }}
              >✕</span>
            )}
          </div>
        ))}
        <button style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: 13 }} onClick={newCategory}>+ New Category</button>
      </div>

      {/* Drop zone — targets the selected folder */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (selectedCat) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => { if (selectedCat) fileInputRef.current?.click(); }}
        style={{
          border: `2px dashed ${dragOver ? colors.orange : colors.border}`,
          background: dragOver ? '#FFF7ED' : colors.graySoft,
          borderRadius: 8, padding: '24px 16px', textAlign: 'center',
          cursor: selectedCat ? 'pointer' : 'not-allowed', marginBottom: 16,
          opacity: selectedCat ? 1 : 0.7, transition: 'all 0.15s'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => uploadFiles(e.target.files)}
          accept=".pdf,.ppt,.pptx,.key,.doc,.docx,.xls,.xlsx,.xlsm,.png,.jpg,.jpeg,.gif,.mp4,.mov,.zip"
        />
        {uploading ? (
          <div style={{ color: colors.orangeDark, fontSize: 14 }}>Uploading…</div>
        ) : selectedCat ? (
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>⬆</div>
            Drop files into <strong style={{ color: colors.charcoal }}>{catIcon(selectedCat)} {selectedCat}</strong>, or click to browse
            <div style={{ fontSize: 11, marginTop: 4 }}>PPT, PDF, Word, Excel, images, video, zip — up to 100 MB each. Drop several at once.</div>
          </div>
        ) : (
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>📂</div>
            Pick a category above (or <span style={{ color: colors.orange, fontWeight: 700, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); newCategory(); }}>+ create one</span>), then drop files into it.
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...styles.input, maxWidth: isMobile ? '100%' : 340, marginBottom: 0 }}
          placeholder="🔍 Search title, description, filename…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ color: colors.textMuted, fontSize: 13 }}>
          {files.length} file{files.length === 1 ? '' : 's'}{selectedCat ? ` in ${selectedCat}` : ''}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: colors.textMuted }}>Loading…</div>
      ) : files.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', color: colors.textMuted, padding: 30 }}>
          {selectedCat ? `No files in ${selectedCat} yet. Drop one above.` : 'No files yet. Pick a category and drop a presentation or PDF above to get started.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {files.map(f => (
            <div key={f.id} style={{ ...styles.card, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontSize: 30, lineHeight: 1 }}>{extIcon(f.original_name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: colors.charcoal, fontSize: 15, wordBreak: 'break-word' }}>{f.title}</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                    {catIcon(f.category)} {f.category} · {fmtSize(f.size_bytes)}
                  </div>
                </div>
              </div>
              {f.description && (
                <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.4 }}>{f.description}</div>
              )}
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {f.uploaded_by_name || 'Unknown'} · {fmtDate(f.created_at)} · ⬇ {f.download_count}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 'auto', flexWrap: 'wrap' }}>
                <button style={{ ...styles.btn, padding: '6px 14px', fontSize: 13 }} onClick={() => doDownload(f)}>⬇ Download</button>
                {canManage && (
                  <>
                    <button style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: 13 }} onClick={() => doRename(f)}>Rename</button>
                    <button style={{ ...styles.btnDanger, padding: '6px 12px', fontSize: 13 }} onClick={() => doDelete(f)}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
